import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { mergeDeepRight, pathOr } from 'ramda'
import { chatbotEvents } from '../state'
import { portfolioApiEvents, portfolioApiConstants } from 'modules/api/portfolio'

const { commissionUndertakings } = portfolioApiConstants

export interface LeaseCommissionState {
  managementFee: {
    type: string
    monthlyRentAmount: string
    netAmount: {
      value: string
      vatable: boolean
    }
    percentage: {
      value: string
      vatable: boolean
    }
    splits: any[]
  }
  procurementFee: {
    type: string
    monthlyRentAmount: string
    netAmount: {
      value: string
      vatable: boolean
    }
    percentage: {
      value: string
      vatable: boolean
    }
    splits: any[]
  }
  id: string | null
}

export const initialState: LeaseCommissionState = {
  managementFee: {
    type: commissionUndertakings.variable,
    monthlyRentAmount: '',
    netAmount: {
      value: '',
      vatable: false,
    },
    percentage: {
      value: '',
      vatable: false,
    },
    splits: [],
  },
  procurementFee: {
    type: commissionUndertakings.variable,
    monthlyRentAmount: '',
    netAmount: {
      value: '',
      vatable: false,
    },
    percentage: {
      value: '',
      vatable: false,
    },
    splits: [],
  },
  id: null,
}

const leaseCommissionSlice = createSlice({
  name: 'chatbot/dialogs/leaseCommission',
  initialState,
  reducers: {
    commissionSaved: (state, action: PayloadAction<Partial<LeaseCommissionState>>) => {
      Object.assign(state, action.payload)
    },
  },
  extraReducers: builder => {
    builder
      .addCase(portfolioApiEvents.amendCommission_success, (state, action) => {
        const commission = action.payload.commission
        const managementFee = mergeDeepRight(initialState.managementFee, pathOr({}, ['managementFee'], commission))
        const procurementFee = mergeDeepRight(initialState.procurementFee, pathOr({}, ['procurementFee'], commission))

        state.managementFee = managementFee as LeaseCommissionState['managementFee']
        state.procurementFee = procurementFee as LeaseCommissionState['procurementFee']
      })
      .addCase(chatbotEvents.setCurrentPortfolio, (state, action) => {
        const commission = action.payload?.commission
        const managementFee = mergeDeepRight(initialState.managementFee, pathOr({}, ['managementFee'], commission))
        const procurementFee = mergeDeepRight(initialState.procurementFee, pathOr({}, ['procurementFee'], commission))

        // Preserve existing VAT state if not provided in payload
        state.managementFee = {
          ...managementFee,
          netAmount: {
            ...managementFee.netAmount,
            vatable: managementFee.netAmount?.vatable ?? state.managementFee.netAmount.vatable,
          },
          percentage: {
            ...managementFee.percentage,
            vatable: managementFee.percentage?.vatable ?? state.managementFee.percentage.vatable,
          },
        } as LeaseCommissionState['managementFee']
        state.procurementFee = {
          ...procurementFee,
          netAmount: {
            ...procurementFee.netAmount,
            vatable: procurementFee.netAmount?.vatable ?? state.procurementFee.netAmount.vatable,
          },
          percentage: {
            ...procurementFee.percentage,
            vatable: procurementFee.percentage?.vatable ?? state.procurementFee.percentage.vatable,
          },
        } as LeaseCommissionState['procurementFee']
      })
      .addCase(chatbotEvents.resetCurrentPortfolio, state => {
        // CRITICAL: Preserve all 4 VAT flags across reset
        const preservedVat = {
          mgmtNet: state.managementFee.netAmount.vatable,
          mgmtPct: state.managementFee.percentage.vatable,
          procNet: state.procurementFee.netAmount.vatable,
          procPct: state.procurementFee.percentage.vatable,
        }
        // Reset id
        state.id = initialState.id
        // Create new nested objects with preserved VAT values
        state.managementFee = {
          type: commissionUndertakings.variable,
          monthlyRentAmount: '',
          netAmount: { value: '', vatable: preservedVat.mgmtNet },
          percentage: { value: '', vatable: preservedVat.mgmtPct },
          splits: [],
        }
        state.procurementFee = {
          type: commissionUndertakings.variable,
          monthlyRentAmount: '',
          netAmount: { value: '', vatable: preservedVat.procNet },
          percentage: { value: '', vatable: preservedVat.procPct },
          splits: [],
        }
      })
  },
})

export const { reducer } = leaseCommissionSlice
export const leaseCommissionEvents = leaseCommissionSlice.actions
